/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import { FieldArrayRenderProps } from 'react-final-form-arrays'
import { useParams } from 'react-router-dom'
import { Accordion, AccordionDetails, AccordionSummary, Button, IconButton, Typography } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { EditorField } from 'components/Editor'
import { NumberInputField } from 'components/Input'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg'
import { ConfirmationModal } from 'components/Modals/ConfirmationModal/ConfirmationModal'
import { validateRequired, validateRequiredHtml, validateRequiredNumber } from 'utils/validation'
import { validatePTMinScore } from 'pagesNew/PracticeTests/utils'
import { SelectField } from 'components/Select'
import * as classes from './styles'
import { SCORE_ITEMS } from './consts'

type TestResult = {
  id?: string
  resultPercantage?: number
  DATScore?: number
  resultTextAsHtml?: string
  resultTextAsJson?: string
}

export type TestResultsFieldArrayProps = {
  fields: FieldArrayRenderProps<TestResult, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<TestResult, HTMLElement>['meta']
  passFailStandardizedScore: boolean
}

export const TestResultsFieldArray = ({ fields, meta, passFailStandardizedScore }: TestResultsFieldArrayProps) => {
  const { platform } = useParams()
  const [indexToRemove, setIndexToRemove] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)

  const platformSlug = platform as string

  const handleAdd = () => {
    fields.push({})
    setExpanded(fields.value?.length ?? 1)
  }

  const handleConfirm = () => {
    if (indexToRemove !== null) {
      setExpanded(null)
      setIndexToRemove(null)
      fields.remove(indexToRemove)
    }
  }

  return (
    <div>
      <Typography variant="body2" css={classes.label}>
        Graduation Table
      </Typography>
      <div>
        {fields.map((field, index) => {
          const isExpanded = expanded === index
          const value = fields.value?.[index]

          return (
            <Accordion
              key={field}
              expanded={isExpanded}
              css={[classes.accordion, meta.error?.[index] && meta.touched && classes.accordionError]}
            >
              <AccordionSummary css={classes.accordionSummary}>
                <div css={classes.textWrapper}>
                  <Typography variant="body2" css={classes.label}>
                    From
                  </Typography>
                  <NumberInputField
                    name={`${field}.resultPercantage`}
                    validate={validateRequiredNumber}
                    css={classes.numberInput(value?.resultPercantage ? String(value.resultPercantage) : null)}
                    disabled={!index}
                    data-testid="test-result-percantage"
                  />
                  <Typography variant="body2" css={classes.label}>
                    % score, display this message:
                  </Typography>
                </div>
                <div css={classes.textWrapper}>
                  {!!index && (
                    <Button
                      variant="outlined"
                      color="red"
                      size="small"
                      onClick={() => setIndexToRemove(index)}
                      css={classes.iconButton}
                      data-testid="remove-test-result"
                    >
                      <TrashIcon />
                    </Button>
                  )}
                  <Typography variant="body2" css={classes.label}>
                    Standardized Score:
                  </Typography>
                  {passFailStandardizedScore ? (
                    <SelectField
                      name={`${field}.DATScore`}
                      items={SCORE_ITEMS}
                      validate={validateRequired}
                      css={[classes.numberInput(value?.DATScore ? String(value.DATScore) : null), classes.DATSelect]}
                      popperCss={classes.DATSelectPopper}
                      data-testid="test-result-set-score"
                    />
                  ) : (
                    <NumberInputField
                      name={`${field}.DATScore`}
                      validate={validatePTMinScore(platformSlug)}
                      css={classes.numberInput(value?.DATScore ? String(value.DATScore) : null)}
                    />
                  )}
                  <IconButton onClick={() => setExpanded(isExpanded ? null : index)}>
                    <ChevronDownIcon
                      css={[classes.iconChevron, isExpanded && classes.iconChevronOpen]}
                      data-testid="expand-test-result"
                    />
                  </IconButton>
                </div>
              </AccordionSummary>
              <AccordionDetails css={classes.accordionDetails}>
                <div css={classes.editor}>
                  <EditorField
                    type="default"
                    name={`${field}.resultTextAsHtml`}
                    placeholder="Enter Result Text"
                    validate={validateRequiredHtml}
                  />
                </div>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </div>
      <Button variant="outlined" color="blue" size="large" onClick={handleAdd} css={classes.addContentButton}>
        Add Graduation <AddRoundedIcon />
      </Button>
      <ConfirmationModal
        open={indexToRemove !== null}
        title={`Deleting Graduation item #${(indexToRemove ?? 0) + 1}`}
        subtitle={`Are you sure you want to delete this Graduation item?`}
        onClose={() => setIndexToRemove(null)}
        onConfirm={handleConfirm}
        confirmText="Delete"
        closeText="Cancel"
      />
    </div>
  )
}
